import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentCategory, useGeneratePosterFromVideoAttachment, VideoAttachment } from '@bonusly/common-action-input';
import { useToast } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';

interface Props {
	onErrorCallback?: (error: any) => void;
	onSettledCallback?: () => void;
}

export const useGenerateVideoForAttachment = ({ onErrorCallback, onSettledCallback }: Props) => {
	const http = useHttpClient('v2');
	const { t } = useTranslation('recognition');
	const toast = useToast();
	const abortControllerRef = useRef<AbortController | null>(null);
	const generatePoster = useGeneratePosterFromVideoAttachment();

	const mutation = useMutation<VideoAttachment, Error, { file: File; posterFile?: File | null }>(
		async ({ file, posterFile }) => {
			abortControllerRef.current = new AbortController();
			const form = new FormData();
			form.append('video[name]', file.name);
			form.append('video[file]', file);
			// If the poster file is not provided, generate it. Note, the poster
			// is an video HTML attribute that browsers use to render a still image
			// from the video. It is required for some browsers, specifically Safari mobile.
			if (!posterFile) {
				posterFile = (await generatePoster(file)).posterFile;
			}
			// Ok, if we have a poster file (either provided, or generated above),
			// use it when we post video content.
			if (posterFile) {
				form.append('video[poster]', posterFile);
			}

			const response = await http.post('/videos', form, {
				signal: abortControllerRef.current.signal,
			});

			const video = response.data;
			return new VideoAttachment({
				name: video.name,
				category: AttachmentCategory.Video,
				video_id: video.id,
				video_poster_url: video.video_poster_url,
				video_hd_url: video.video_hd_url,
				video_sd_url: video.video_sd_url,
				video_original_url: video.video_original_url,
			});
		},
		{
			onSettled: onSettledCallback,
			onError: (error: any) => {
				if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
					toast({
						title: t('bonus_input.video.cancel_error'),
						status: 'info',
					});
					return;
				}

				const errorMessage = error?.response?.data?.error || error?.message || 'Attachment failed.';
				toast({
					title: t('bonus_input.video.upload_error', { error: errorMessage }),
					status: 'error',
				});

				if (onErrorCallback) {
					onErrorCallback(errorMessage);
				}
			},
		}
	);

	const generateVideoForAttachment = (file: File, posterFile?: File | null) => {
		return mutation.mutateAsync({ file, posterFile }).catch((error) => {
			if (error.name !== 'AbortError' || error?.name === 'CanceledError') {
				console.error('Unhandled error:', error);
			}
		});
	};

	const cancelVideoGeneration = () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
	};

	return {
		generateVideoForAttachment,
		cancelVideoGeneration,
		isLoading: mutation.isLoading,
	};
};
